import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Admin, Auth, Tenants, type Membership, type Role } from '@/lib/api/v1';
import { Button, Card, EmptyState, Icon, Input, Modal, Select, Skeleton, Badge } from '@/design-system';
import { useSession, usePrefs, useToasts } from '@/lib/store';
import { planLabel, type PlanId } from '@/lib/entitlements';

export function AdminPage() {
  const { t: _t } = useTranslation();
  const { user, memberships, role } = useSession();
  const { activeTenantId } = usePrefs();
  const tenantsQ = useQuery({ queryKey: ['tenants'], queryFn: () => Tenants.list() });
  const tenants = tenantsQ.data?.tenants ?? [];
  const activeTenant = tenants.find((t) => t.id === activeTenantId) ?? tenants[0];
  const myMembership = memberships.find((m) => m.tenantId === activeTenant?.id);
  const isOwner = myMembership?.role === 'OWNER';
  const isSuperAdmin = role === 'SUPER_ADMIN';

  if (!user) return null;

  return (
    <div className="grid gap-8 max-w-4xl">
      <header>
        <h1 className="ui-section-title text-3xl">Husholdning</h1>
        {activeTenant && (
          <p className="text-sm text-mist">
            {activeTenant.name} · plan <strong className="text-parchment">{planLabel(activeTenant.plan)}</strong>
          </p>
        )}
      </header>

      {!activeTenant ? (
        <EmptyState icon="container" title="Ingen aktiv husholdning" description="Bytt eller opprett en husholdning fra menyen til venstre." />
      ) : (
        <>
          <MembersCard tenantId={activeTenant.id} canManage={isOwner} myUserId={user.id} />
          <InvitationsCard tenantId={activeTenant.id} canManage={isOwner} />
          <PersonalTokensCard />
          {isSuperAdmin && <SuperAdminPlanCard />}
          <Card className="grid gap-2 p-5">
            <div className="flex items-center gap-3">
              <Icon name="refresh" className="text-candle" />
              <h2 className="font-serif text-xl text-parchment">Katalogsync</h2>
              <Link
                to="/admin/sync"
                className="ml-auto text-xs uppercase tracking-widest text-candle hover:underline"
              >
                Åpne →
              </Link>
            </div>
            <p className="text-xs text-mist">
              Velg hva som skal synces fra Vinmonopolet, WineAPI og lokal LWIN-fil.
            </p>
          </Card>
          <Card className="grid gap-2 p-5">
            <div className="flex items-center gap-3">
              <Icon name="container" className="text-candle" />
              <h2 className="font-serif text-xl text-parchment">Jobber</h2>
              <Link
                to="/admin/jobs"
                className="ml-auto text-xs uppercase tracking-widest text-candle hover:underline"
              >
                Åpne →
              </Link>
            </div>
            <p className="text-xs text-mist">
              Bakgrunnsjobber: katalogsynk, vinmonopol-skanninger, paring, anbefalinger.
            </p>
          </Card>
          <Card className="grid gap-2 p-5">
            <div className="flex items-center gap-3">
              <Icon name="sparkles" className="text-candle" />
              <h2 className="font-serif text-xl text-parchment">Integrasjoner</h2>
              <Link
                to="/admin/providers"
                className="ml-auto text-xs uppercase tracking-widest text-candle hover:underline"
              >
                Åpne →
              </Link>
            </div>
            <p className="text-xs text-mist">
              Status for strekkode-tjenester, AI-modeller, print-kø og berikelses-providere.
            </p>
          </Card>
        </>
      )}
    </div>
  );
}

const PLAN_OPTIONS: { value: PlanId; label: string }[] = [
  { value: 'free', label: 'Gratis' },
  { value: 'pluss', label: 'Pluss' },
  { value: 'pro', label: 'Pro' },
  { value: 'legacy', label: 'Legacy (full tilgang, grandfathered)' },
];

/**
 * Superadmin-only manual plan override — see backend/src/routes/auth.ts
 * `PATCH /api/v1/admin/tenants/:id/plan` (requireRole("SUPER_ADMIN")). Takes
 * an arbitrary tenant id (not just the current household) since this is a
 * platform-wide support tool, not scoped to the admin's own tenant.
 */
function SuperAdminPlanCard() {
  const { push } = useToasts();
  const [tenantId, setTenantId] = useState('');
  const [plan, setPlan] = useState<PlanId>('pro');
  const [result, setResult] = useState<string | null>(null);

  const setTenantPlan = useMutation({
    mutationFn: () => Admin.setTenantPlan(tenantId.trim(), { plan }),
    onSuccess: (tenant) => {
      setResult(`${tenant.name} (${tenant.id}) er nå på plan "${planLabel(tenant.plan)}".`);
      push({ title: 'Plan oppdatert', description: `${tenant.name} → ${planLabel(tenant.plan)}`, tone: 'success' });
    },
    onError: (err: Error) => {
      setResult(null);
      push({ title: 'Kunne ikke endre plan', description: err.message, tone: 'danger' });
    },
  });

  return (
    <Card className="grid gap-4 border-candle/30 p-5">
      <div className="flex items-center gap-3">
        <Icon name="sparkles" className="text-candle" />
        <h2 className="font-serif text-xl text-parchment">Superadmin: Sett tenant-plan</h2>
        <Badge tone="gold" className="ml-auto">SUPER_ADMIN</Badge>
      </div>
      <p className="text-xs text-mist">
        Manuell override av abonnementsplan for en hvilken som helst tenant (kundestøtte, gratis Pro-trial til
        restauranter, o.l.). Går forbi betalingsflyten helt — bruk med omhu.
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <Input label="Tenant-ID" value={tenantId} onChange={(e) => setTenantId(e.target.value)} placeholder="f.eks. cellar-abc123" className="min-w-[220px]" />
        <Select
          label="Plan"
          value={plan}
          onChange={(e) => setPlan(e.target.value as PlanId)}
          options={PLAN_OPTIONS}
        />
        <Button
          variant="primary"
          loading={setTenantPlan.isPending}
          disabled={!tenantId.trim()}
          onClick={() => setTenantPlan.mutate()}
        >
          Sett plan
        </Button>
      </div>
      {result && <p className="text-xs text-mist">{result}</p>}
    </Card>
  );
}

function MembersCard({ tenantId, canManage, myUserId }: { tenantId: string; canManage: boolean; myUserId: string }) {
  const qc = useQueryClient();
  const { push } = useToasts();
  const membersQ = useQuery({
    queryKey: ['members', tenantId],
    queryFn: () => Tenants.members(tenantId),
  });
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'OWNER' | 'MEMBER' | 'GUEST'>('MEMBER');
  const [lastInviteLink, setLastInviteLink] = useState<string | null>(null);

  const invite = useMutation({
    mutationFn: () => Tenants.invite(tenantId, { email: inviteEmail, role: inviteRole }),
    onSuccess: (inv) => {
      const url = `${window.location.origin}/invite/${inv.token}`;
      setLastInviteLink(url);
      push({ title: 'Invitasjon opprettet', description: 'Lenken er kopiert til utklippstavlen', tone: 'success' });
      void navigator.clipboard.writeText(url).catch(() => undefined);
      void qc.invalidateQueries({ queryKey: ['members', tenantId] });
      void qc.invalidateQueries({ queryKey: ['invitations', tenantId] });
    },
    onError: (err: Error) =>
      push({ title: 'Kunne ikke invitere', description: err.message, tone: 'danger' }),
  });

  const remove = useMutation({
    mutationFn: (userId: string) => Tenants.removeMember(tenantId, userId),
    onSuccess: () => {
      push({ title: 'Medlem fjernet', tone: 'success' });
      void qc.invalidateQueries({ queryKey: ['members', tenantId] });
    },
  });

  const updateRole = useMutation({
    mutationFn: (input: { userId: string; role: Role }) =>
      Tenants.updateMemberRole(tenantId, input.userId, input.role),
    onSuccess: () => {
      push({ title: 'Rolle oppdatert', tone: 'success' });
      void qc.invalidateQueries({ queryKey: ['members', tenantId] });
    },
    onError: (err: Error) =>
      push({ title: 'Kunne ikke endre rolle', description: err.message, tone: 'danger' }),
  });

  return (
    <Card className="grid gap-4 p-5">
      <div className="flex items-center gap-3">
        <Icon name="users" className="text-candle" />
        <h2 className="font-serif text-xl text-parchment">Medlemmer</h2>
        {canManage && (
          <Button className="ml-auto" size="sm" iconLeft="plus" onClick={() => { setInviteOpen(true); setLastInviteLink(null); }}>
            Inviter
          </Button>
        )}
      </div>

      {membersQ.isLoading ? (
        <div className="grid gap-2"><Skeleton height={48} /><Skeleton height={48} /></div>
      ) : (
        <ul className="grid gap-2">
          {(membersQ.data ?? []).map((m: Membership) => (
            <li key={m.id} className="flex items-center gap-3 rounded-md border border-line p-3">
              <div className="grid h-9 w-9 place-items-center rounded-full bg-candle/10 text-candle text-xs uppercase">
                {(m.email[0] ?? '?').toUpperCase()}
              </div>
              <div className="grid flex-1">
                <span className="text-sm text-parchment">{m.email}</span>
                <span className="text-[10px] uppercase tracking-widest text-mist">Medlem siden {new Date(m.joinedAt).toLocaleDateString('nb-NO')}</span>
              </div>
              {canManage && m.userId !== myUserId ? (
                <select
                  value={m.role}
                  onChange={(e) => updateRole.mutate({ userId: m.userId, role: e.target.value as Role })}
                  disabled={updateRole.isPending}
                  aria-label={`Rolle for ${m.email}`}
                  className="h-8 rounded-md border border-line bg-ink-soft px-2 text-xs uppercase tracking-widest text-parchment focus:border-candle focus:outline-none"
                >
                  <option value="OWNER">OWNER</option>
                  <option value="MEMBER">MEMBER</option>
                  <option value="GUEST">GUEST</option>
                </select>
              ) : (
                <Badge tone={m.role === 'OWNER' ? 'gold' : 'mist'}>{m.role}</Badge>
              )}
              {canManage && m.userId !== myUserId && (
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm('Fjerne dette medlemmet?')) remove.mutate(m.userId);
                  }}
                  className="text-mist hover:text-danger"
                  aria-label="Fjern"
                >
                  <Icon name="trash" size={14} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <Modal open={inviteOpen} onClose={() => setInviteOpen(false)} title="Inviter medlem" size="sm">
        <div className="grid gap-4">
          <Input
            iconLeft="mail"
            label="E-post"
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            autoFocus
          />
          <div className="grid gap-1.5">
            <label className="text-xs uppercase tracking-widest text-mist">Rolle</label>
            <div className="flex gap-2">
              {(['OWNER', 'MEMBER', 'GUEST'] as const).map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setInviteRole(r)}
                  className={`ui-pill ${inviteRole === r ? 'ui-pill--active' : ''}`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
          {lastInviteLink && (
            <div className="rounded-md border border-line bg-ink-soft p-3 text-xs">
              <div className="text-[10px] uppercase tracking-widest text-mist">Invitasjonslenke (kopiert)</div>
              <div className="mt-1 truncate font-mono text-candle">{lastInviteLink}</div>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setInviteOpen(false)}>Lukk</Button>
            <Button
              variant="primary"
              iconRight="arrow-right"
              loading={invite.isPending}
              disabled={!/^[^@\s]+@[^@\s]+$/.test(inviteEmail)}
              onClick={() => invite.mutate()}
            >
              Send invitasjon
            </Button>
          </div>
        </div>
      </Modal>
    </Card>
  );
}

function InvitationsCard({ tenantId, canManage }: { tenantId: string; canManage: boolean }) {
  const qc = useQueryClient();
  const { push } = useToasts();

  const invitationsQ = useQuery({
    queryKey: ['invitations', tenantId],
    queryFn: () => Tenants.invitations(tenantId),
    refetchOnWindowFocus: true,
  });

  const revoke = useMutation({
    mutationFn: (invitationId: string) => Tenants.revokeInvitation(tenantId, invitationId),
    onSuccess: () => {
      push({ title: 'Invitasjon trukket tilbake', tone: 'success' });
      void qc.invalidateQueries({ queryKey: ['invitations', tenantId] });
    },
    onError: (err: Error) =>
      push({ title: 'Kunne ikke tilbakekalle', description: err.message, tone: 'danger' }),
  });

  const invitations = invitationsQ.data ?? [];
  const now = Date.now();

  return (
    <Card className="grid gap-4 p-5">
      <div className="flex items-center gap-3">
        <Icon name="mail" className="text-candle" />
        <h2 className="font-serif text-xl text-parchment">Ventende invitasjoner</h2>
        {invitations.length > 0 && (
          <Badge tone="mist" className="ml-auto">{invitations.length}</Badge>
        )}
      </div>

      {invitationsQ.isLoading ? (
        <Skeleton height={48} />
      ) : invitations.length === 0 ? (
        <p className="text-xs text-mist">Ingen ventende invitasjoner.</p>
      ) : (
        <ul className="grid gap-2">
          {invitations.map((inv) => {
            const expired = new Date(inv.expiresAt).getTime() < now;
            const link = `${window.location.origin}/invite/${inv.token}`;
            return (
              <li key={inv.id} className="flex items-center gap-3 rounded-md border border-line p-3">
                <Icon name="mail" size={14} className={expired ? 'text-mist/40' : 'text-candle'} />
                <div className="grid flex-1 min-w-0">
                  <span className="truncate text-sm text-parchment">{inv.email}</span>
                  <span className="text-[10px] uppercase tracking-widest text-mist">
                    {inv.role} ·{' '}
                    {expired
                      ? `Utløpt ${new Date(inv.expiresAt).toLocaleDateString('nb-NO')}`
                      : `Utløper ${new Date(inv.expiresAt).toLocaleDateString('nb-NO')}`}
                  </span>
                </div>
                <Badge tone={expired ? 'mist' : 'gold'}>{expired ? 'utløpt' : 'venter'}</Badge>
                <button
                  type="button"
                  onClick={() => {
                    void navigator.clipboard.writeText(link);
                    push({ title: 'Lenke kopiert', tone: 'success' });
                  }}
                  className="text-mist hover:text-candle"
                  aria-label="Kopier lenke"
                  title="Kopier lenke"
                >
                  <Icon name="copy" size={14} />
                </button>
                {canManage && (
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm('Trekke tilbake denne invitasjonen?')) revoke.mutate(inv.id);
                    }}
                    className="text-mist hover:text-[color:var(--ui-color-danger)]"
                    aria-label="Tilbakekall"
                  >
                    <Icon name="trash" size={14} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function PersonalTokensCard() {
  const qc = useQueryClient();
  const { push } = useToasts();
  const [name, setName] = useState('Min CLI');
  const [created, setCreated] = useState<string | null>(null);

  // Try to list tokens — backend may not support GET yet, in which case we
  // tolerate the 404/405 and render a clear note so the page never breaks.
  const tokensQ = useQuery({
    queryKey: ['personal-tokens'],
    queryFn: async () => {
      try {
        return await Auth.listPersonalTokens();
      } catch (err) {
        const status = (err as { status?: number })?.status;
        if (status === 404 || status === 405) return null;
        throw err;
      }
    },
  });

  const create = useMutation({
    mutationFn: () => Auth.createPersonalToken({ name }),
    onSuccess: (token) => {
      setCreated(token.token ?? null);
      push({ title: 'Token opprettet', description: 'Kopier den nå — vi viser den ikke igjen', tone: 'success' });
      void qc.invalidateQueries({ queryKey: ['personal-tokens'] });
    },
    onError: (err: Error) =>
      push({ title: 'Kunne ikke opprette token', description: err.message, tone: 'danger' }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => Auth.deletePersonalToken(id),
    onSuccess: () => {
      push({ title: 'Token tilbakekalt', tone: 'success' });
      void qc.invalidateQueries({ queryKey: ['personal-tokens'] });
    },
    onError: (err: Error) =>
      push({ title: 'Kunne ikke tilbakekalle', description: err.message, tone: 'danger' }),
  });

  const tokens = tokensQ.data;

  return (
    <Card className="grid gap-4 p-5">
      <div className="flex items-center gap-3">
        <Icon name="lock" className="text-candle" />
        <h2 className="font-serif text-xl text-parchment">Personlige tokens</h2>
      </div>
      <p className="text-xs text-mist">For maskin-tilgang (CLI, scripts, integrasjoner). Vis aldri tokenet til andre.</p>

      <div className="flex flex-wrap items-end gap-2">
        <Input label="Beskrivelse" value={name} onChange={(e) => setName(e.target.value)} />
        <Button variant="primary" iconLeft="plus" loading={create.isPending} onClick={() => create.mutate()}>
          Opprett token
        </Button>
      </div>

      {created && (
        <div className="rounded-md border border-candle/40 bg-candle/5 p-3">
          <div className="mb-1 text-xs uppercase tracking-widest text-candle">Vises kun nå</div>
          <code className="break-all text-sm text-parchment">{created}</code>
          <div className="mt-2 flex gap-2">
            <Button
              size="sm"
              variant="ghost"
              iconLeft="copy"
              onClick={() => {
                void navigator.clipboard.writeText(created);
                push({ title: 'Kopiert', tone: 'success' });
              }}
            >
              Kopier
            </Button>
            <Button size="sm" variant="ghost" iconLeft="x" onClick={() => setCreated(null)}>
              Skjul
            </Button>
          </div>
        </div>
      )}

      {tokensQ.isLoading ? (
        <Skeleton height={48} />
      ) : tokens === null ? (
        <p className="text-[10px] uppercase tracking-widest text-mist/70">
          Backend støtter ikke <code>GET /api/v1/me/personal-tokens</code> enda. Tokens du oppretter vises kun ved
          opprettelse — bevar dem trygt.
        </p>
      ) : tokens && tokens.length > 0 ? (
        <ul className="grid gap-2">
          {tokens.map((tok) => (
            <li key={tok.id} className="flex items-center gap-3 rounded-md border border-line p-3 text-sm">
              <Icon name="lock" size={14} className="text-candle" />
              <div className="grid flex-1">
                <span className="text-parchment">{tok.name}</span>
                <span className="text-[10px] uppercase tracking-widest text-mist">
                  {tok.prefix}… · opprettet {new Date(tok.createdAt).toLocaleDateString('nb-NO')}
                  {tok.expiresAt
                    ? ` · utløper ${new Date(tok.expiresAt).toLocaleDateString('nb-NO')}`
                    : ' · ingen utløp'}
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (window.confirm(`Tilbakekalle token "${tok.name}"?`)) remove.mutate(tok.id);
                }}
                className="text-mist hover:text-[color:var(--ui-color-danger)]"
                aria-label="Tilbakekall"
              >
                <Icon name="trash" size={14} />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-mist">Ingen tokens enda.</p>
      )}
    </Card>
  );
}
